/* eslint-disable max-lines-per-function */
import GTM from 'extensions/gtm';
import {defineCustomElement, joinUrl} from 'extensions/utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {ref} from 'lit/directives/ref.js';
import styles from './x-product-card.component.scss?as=stylesheet';

const NO_IMAGE_THUMBNAIL = `${window.mivaJS.theme_path}/public/images/square-thumbnail.jpg`;

export class ProductCard extends LitElement {
	static styles = [styles];

	static get properties () {
		return {
			_brand: {
				attribute: 'brand',
				type: String
			},
			_incartPricing: {
				attribute: 'incart-pricing',
				type: Boolean
			},
			_name: {
				attribute: 'name',
				type: String
			},
			_startingAt: {
				attribute: 'starting-at',
				type: String
			},
			_variantSwatch: {
				attribute: 'variant-swatch',
				type: Object
			},
			code: {
				type: String
			},
			discountedBrand: {
				attribute: 'discounted-brand',
				type: Boolean
			},
			flag: {
				type: String
			},
			formattedPrice: {
				attribute: 'formatted-price',
				type: String
			},
			formattedRetail: {
				attribute: 'formatted-retail',
				type: String
			},
			gtmList: {
				attribute: 'gtm-list',
				type: String
			},
			hasMetalOptions: {
				attribute: 'has-metal-options',
				type: Boolean
			},
			hoverImageUrl: {
				attribute: 'hover-image-url',
				type: String
			},
			imageHeight: {
				attribute: 'image-height',
				type: Number
			},
			imageUrl: {
				attribute: 'image-url',
				type: String
			},
			imageWidth: {
				attribute: 'image-width',
				type: Number
			},
			price: {
				type: Number
			},
			retail: {
				type: Number
			},
			saleBrands: {
				attribute: 'sale-brands',
				type: Array
			},
			selectedSwatch: {
				type: Object
			},
			simple: {
				type: Boolean
			},
			sourceLoaded: {
				state: true,
				type: Boolean
			},
			url: {
				type: String
			}
		};
	}

	/**
	 * @type {boolean}
	 */
	get incartPricing () {
		return this._incartPricing === ''
			? this.saleBrands?.find(brand => brand === this.brand)
			: this._incartPricing;
	}

	/**
	 * @type {string}
	 */
	get startingAt () {
		if (this._startingAt !== '' && this._startingAt !== '$0.00') {
			return this._startingAt;
		}

		if (this._startingAt === '$0.00' || this._startingAt === 'Click for Details') {
			return 'Click for Details';
		}

		return '';
	}

	get variantSwatches () {
		const swatches = [];

		if (this._variantSwatch) {
			for (const swatch of this._variantSwatch) {
				if (swatch.code === this.code) {
					swatches.unshift(swatch);
					if (!this.selectedSwatch?.code) {
						this.selectedSwatch = swatch;
					}

					continue;
				}

				swatches.push(swatch);
			}
		}

		return swatches;
	}

	updated (changedProperties) {
		super.updated();

		if (changedProperties.has('code') && changedProperties.code !== this.code) {
			this.selectedSwatch = {};
		}
	}

	get brand () {
		return this._brand || 'Borsheims';
	}

	get name () {
		const name = this.selectedSwatch?.product_name ?? this._name;
		return name.replace(this.brand, '');
	}

	get formattedUrl () {
		const baseUrl = this.selectedSwatch?.variant_id ? this.url.replace(/(variant_id=).*?(&|$)/, '$1' + this.selectedSwatch.variant_id + '$2') : this.url;

		return joinUrl(baseUrl, [
			...this.gtmList
				? [
					// eslint-disable-next-line array-element-newline
					['list', this.gtmList]
				]
				: []
		]);
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		// Default property values
		this._brand = '';
		this._incartPricing = false;
		this._name = '';
		this._startingAt = '';
		this._variantSwatch = [];
		this.code = '';
		this.discountedBrand = false;
		this.formattedPrice = '';
		this.formattedRetail = '';
		this.gtmList = '';
		this.hasMetalOptions = false;
		this.hoverImageUrl = '';
		this.imageHeight = 200;
		this.imageType = 'main';
		this.imageUrl = NO_IMAGE_THUMBNAIL;
		this.imageWidth = 200;
		this.price = 0;
		this.retail = 0;
		this.saleBrands = [];
		this.selectedSwatch = {};
		this.simple = false;
		this.sourceLoaded = false;
		this.url = '';
	}

	handleSourceLoad () {
		this.sourceLoaded = true;
	}

	handleImageError (e) {
		const [target] = e.composedPath();

		target.src = NO_IMAGE_THUMBNAIL;
	}

	imageContainerChanged (container) {
		if (!container) {
			return;
		}

		const observer = new MutationObserver((changes) => {
			changes.forEach(change => {
				if (change.attributeName.includes('src')) {
					this.sourceLoaded = container.complete;
				}
			});
		});

		observer.observe(container, {attributes: true});
	}

	renderPrice () {
		if (this.incartPricing) {
			return html`
				<div class="price-sale">
					See Price in Bag
				</div>
			`;
		}

		if (this.startingAt && this.startingAt !== '$0.00') {
			return nothing;
		}

		if (this.discountedBrand) {
			return html`
				<div class="price-sale">
					${this.formattedRetail}
				</div>
			`;
		}

		if (this.formattedPrice === '') {
			return nothing;
		}

		return html`
			<div class="price-sale">
				${this.formattedPrice}
			</div>
		`;
	}

	renderRetailPrice () {
		// If "Click for Details", only show Click for Details
		if (this.startingAt === 'Click for Details') {
			return html`
				<div class="price-retail">
					<span class="price-retail-price">${this.startingAt}</span>
				</div>
			`;
		}

		// If startingAt, and it's not $0.00, display Starting At
		if (this.startingAt && this.startingAt !== '$0.00') {
			return html`
				<div class="price-retail">
					<span class="price-retail-label">Starting At</span>
					<span class="price-retail-price">${this.startingAt}</span>
				</div>
			`;
		}

		if ((this.retail > this.price || this.incartPricing) && !this.discountedBrand) {
			return html`
				<div class="price-retail">
					<span class="price-retail-label">Retail</span>
					<span class="price-retail-price">${this.formattedRetail}</span>
				</div>
			`;
		}

		return nothing;
	}

	renderSwatches () {
		if (this.variantSwatches.length <= 1) {
			return nothing;
		}

		const swatches = [];

		for (const swatch of this.variantSwatches) {
			const swatchClasses = {
				swatch: true,
				'swatch--selected': swatch?.code === this.selectedSwatch?.code
			};

			swatches.push(html`
				<button class="${classMap(swatchClasses)}" @click="${() => this.handleSwatchClick(swatch)}" aria-label="Metal: ${swatch.swatch_metal}">
					<img src="${swatch.swatch_image}" alt="Metal: ${swatch.swatch_metal}" class="swatch-image" loading="lazy" aria-hidden="true" />
				</button>
			`);
		}

		return html`
			<div class="swatches">${swatches}</div>
		`;
	}

	handleSwatchClick (swatch) {
		this.selectedSwatch = swatch;
	}


	handleClick () {
		this.gtm.sendProductClick({
			brand: this.brand,
			id: this.code,
			/* eslint-disable camelcase */
			item_brand: this.brand,
			item_id: this.code,
			item_list_id: this.gtmList,
			item_list_name: this.gtmList,
			item_name: this.name,
			/* eslint-enable camelcase */
			name: this.name,
			price: this.price
		});
	}

	renderMetalOptions () {
		return this.hasMetalOptions
			? html`<div class="has-metal-options">Other metal options available</div>`
			: nothing;
	}

	renderFlag () {
		if (this.flag) {
			return html`<x-product-card-flag type="${this.flag}"></x-product-card-flag>`;
		}

		return nothing;
	}

	render () {
		const containerClasses = {
			container: true,
			'has-hover-image': Boolean(this.hoverImageUrl),
			'image-loaded': this.sourceLoaded,
			simple: this.simple
		};

		const hoverImageClasses = {
			'hover-image': true,
			image: true
		};

		return html`
			<div class="${classMap(containerClasses)}">
				<a href="${this.formattedUrl}" @click="${this.handleClick}" class="top">
					${this.renderFlag()}
					<div class="image-container">
						<div class="image-loader"></div>
						<img ${ref(this.imageContainerChanged)} class="main-image image" alt="${this.name} image" src="${this.selectedSwatch?.product_image ?? this.imageUrl}" height="${this.imageHeight}" width="${this.imageWidth}" loading="lazy" @load="${this.handleSourceLoad}" @error="${this.handleImageError}" />
						${this.hoverImageUrl ? html`<img class="${classMap(hoverImageClasses)}" aria-hidden="true" src="${this.hoverImageUrl}" height="${this.imageHeight}" width="${this.imageWidth}" loading="lazy" fetchpriority="low" @error="${this.handleImageError}" />` : nothing}
					</div>
					<div class="info-container">
						<div class="brand">${this.brand}</div>
						<div class="name">${this.name}</div>
						<div class="price">
							${this.renderPrice()}
							${this.renderRetailPrice()}
							${this.renderMetalOptions()}
						</div>
					</div>
				</a>
				${this.renderSwatches()}
			</div>
		`;
	}
};

defineCustomElement('x-product-card', ProductCard);
